import {
  Alert,
  Box,
  Button,
  Link,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';

const normalizeUrl = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    return new URL(trimmed, window.location.origin).toString();
  } catch {
    return '';
  }
};

const inferType = (url, currentType) => {
  if (currentType === 'pdf' || currentType === 'html') return currentType;
  if (/\.pdf(?:$|[?#])/i.test(url)) return 'pdf';
  return 'html';
};

const ReportViewerPage = () => {
  const [searchParams] = useSearchParams();
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const url = normalizeUrl(searchParams.get('url') || '');
  const type = inferType(url, (searchParams.get('type') || '').toLowerCase());

  const isSameOrigin = useMemo(() => {
    if (!url) return false;
    return new URL(url).origin === window.location.origin;
  }, [url]);

  const shouldRedirectExternalHtml = type === 'html' && !!url && !isSameOrigin;

  useEffect(() => {
    if (!shouldRedirectExternalHtml) return;
    const timer = window.setTimeout(() => {
      window.location.assign(url);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [shouldRedirectExternalHtml, url]);

  const handleCopyLink = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setSnack({ open: true, message: 'Link copiado.', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Falha ao copiar link.', severity: 'error' });
    }
  }, [url]);

  const handleOpenSystemBrowser = useCallback(() => {
    if (!url) return;
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      setSnack({ open: true, message: 'Bloqueado pelo navegador. Tente "Abrir nesta aba".', severity: 'warning' });
    }
  }, [url]);

  const handleOpenSameTab = useCallback(() => {
    if (!url) return;
    window.location.assign(url);
  }, [url]);

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportRelatorioCompleto']}>
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="h6">Visualizador de relatório</Typography>

        {!url ? (
          <Alert severity="error">URL inválida ou ausente.</Alert>
        ) : (
          <>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button size="small" variant="outlined" onClick={handleCopyLink}>
                Copiar link
              </Button>
              <Button size="small" variant="outlined" onClick={handleOpenSystemBrowser}>
                Abrir no navegador do sistema
              </Button>
              <Button size="small" variant="contained" onClick={handleOpenSameTab}>
                Abrir nesta aba
              </Button>
            </Stack>

            {type === 'pdf' && (
              <>
                <Box
                  component="iframe"
                  src={url}
                  title="Visualização de PDF"
                  sx={{ width: '100%', minHeight: '75vh', border: 0, borderRadius: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Se o PDF não carregar,{' '}
                  <Link href={url} target="_self" rel="noreferrer">
                    clique para baixar/abrir
                  </Link>
                  .
                </Typography>
              </>
            )}

            {type === 'html' && isSameOrigin && (
              <Box
                component="iframe"
                src={url}
                title="Visualização HTML"
                sx={{ width: '100%', minHeight: '75vh', border: 0, borderRadius: 1 }}
              />
            )}

            {shouldRedirectExternalHtml && (
              <Alert severity="warning">
                Origem externa detectada. Redirecionando nesta aba...
              </Alert>
            )}
          </>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default ReportViewerPage;
